import React, { useEffect, useState } from "react";

import {
  Calendar,
  Cog,
  ExternalLink,
  LocationMarker,
  OfficeBuilding,
  User,
} from "../../../components/icons/svg/outline";
import { useCoreContext } from "../../../context/global/CoreContext";
import {
  getProjectManagerNames,
  getProjectAddressLabel,
  getProjectCustomerLabel,
  getProjectDateRange,
  getProjectTitle,
} from "../../../utils/business";
import { getProjectStatusConfig } from "../projectsHome/projectHomeHelpers";
import { canManageProjects } from "../shared/projectPermissions";
import { PROJECT_HEADER_SCROLL_COLLAPSE_THRESHOLD } from "../constants";
import { useProjectPageContext } from "./ProjectPageContext";

/**
 * Render the single-project page header with project metadata and actions.
 *
 * @returns {JSX.Element}
 */
const ProjectHeader = () => {
  const { user } = useCoreContext();
  const { project, openEditProject, documentFolders } = useProjectPageContext();
  const projectFolderUrl = documentFolders?.webUrl || "";
  const [isScrolled, setIsScrolled] = useState(false);
  const canEdit = canManageProjects(user);
  const status = getProjectStatusConfig(project, "header");
  const StatusIcon = status.Icon;
  const title = [project.number, getProjectTitle(project)].filter(Boolean).join(" - ");
  const metadataItems = [
    {
      id: "customer",
      Icon: OfficeBuilding,
      label: getProjectCustomerLabel(project),
    },
    {
      id: "address",
      Icon: LocationMarker,
      label: getProjectAddressLabel(project),
    },
    {
      id: "dates",
      Icon: Calendar,
      label: getProjectDateRange(project.date_start, project.date_end),
    },
    {
      id: "manager",
      Icon: User,
      label: getProjectManagerNames(project.managers),
    },
  ];

  useEffect(() => {
    /**
     * Collapse header chrome once scrollY exceeds the project header threshold.
     *
     * @returns {void}
     */
    const handleScroll = () => {
      setIsScrolled(window.scrollY > PROJECT_HEADER_SCROLL_COLLAPSE_THRESHOLD);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header className="sticky top-14 z-40 border-b border-gray-300 bg-gray-200 shadow-sm shadow-gray-300">
      <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-2 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-bold tracking-tight text-gray-950 sm:text-3xl">
                  {title || `Project ${project.id}`}
                </h1>
                <span
                  className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold ${status.className}`}
                >
                  <StatusIcon className="h-3.5 w-3.5" />
                  {status.label}
                </span>
              </div>

              <div
                className={`flex flex-wrap items-center gap-x-5 gap-y-2 overflow-hidden text-sm text-gray-600 transition-all duration-200 ease-out ${
                  isScrolled
                    ? "mt-0 max-h-0 -translate-y-1 opacity-0"
                    : "mt-0 max-h-24 translate-y-0 opacity-100"
                }`}
                aria-hidden={isScrolled}
              >
                {metadataItems.map(({ id, Icon, label }) => (
                  <span key={id} className="inline-flex min-w-0 items-center gap-1.5">
                    <Icon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                    <span className="truncate">{label}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 lg:flex-shrink-0">
            {canEdit ? (
              <button
                type="button"
                onClick={openEditProject}
                className="inline-flex h-8 items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-900 shadow-sm transition hover:border-secondary-200 hover:bg-secondary-50 hover:text-secondary-800 focus:outline-none focus:ring-4 focus:ring-secondary-100"
              >
                <Cog className="h-4 w-4" />
                Edit
              </button>
            ) : null}
            {projectFolderUrl ? (
              <a
                href={projectFolderUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-8 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-300 focus:outline-none focus:ring-4 focus:ring-primary-100"
              >
                <ExternalLink className="h-4 w-4" />
                Project Folder
              </a>
            ) : (
              <button
                type="button"
                disabled
                className="inline-flex h-8 cursor-not-allowed items-center justify-center gap-2 rounded-lg bg-primary/50 px-4 text-sm font-semibold text-white"
                title="Resolving project folder…"
              >
                <ExternalLink className="h-4 w-4" />
                Project Folder
              </button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default ProjectHeader;
